import type {
  AbsenceDto, AbsenceType, AdminSummaryDto, AuthSessionDto, BootstrapDto, CalendarDayDto,
  CommuteLegDto, HistoryDto, NotificationStatusDto, SessionDto, SettingsDto, StatsDto, UserAction, WorkMode,
} from '../types'
import { authHeaders } from './telegram'

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export interface StatisticsFilters {
  mode?: WorkMode
  breakKind?: string
  goalReached?: boolean
  minMinutes?: number
  maxMinutes?: number
}

let sessionCsrfToken: string | undefined
const unauthorizedListeners = new Set<() => void>()

export function subscribeToUnauthorized(listener: () => void) {
  unauthorizedListeners.add(listener)
  return () => { unauthorizedListeners.delete(listener) }
}

export function isAbortError(error: unknown) {
  return error instanceof Error && error.name === 'AbortError'
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(authHeaders())
  if (options.body) headers.set('Content-Type', 'application/json')
  new Headers(options.headers).forEach((value, key) => headers.set(key, value))
  if (isStateChanging(options.method)) {
    const csrfToken = readCookie('XSRF-TOKEN') || sessionCsrfToken
    if (csrfToken) headers.set('X-XSRF-TOKEN', csrfToken)
  }
  const response = await fetch(path, {
    ...options,
    credentials: 'include',
    headers,
  })
  if (!response.ok) {
    if (response.status === 401) unauthorizedListeners.forEach((listener) => listener())
    let message = 'Не удалось выполнить запрос'
    try {
      const problem = await response.json()
      message = problem.detail || problem.title || message
    } catch {
      // Keep the safe fallback for proxy and network errors.
    }
    throw new ApiError(message, response.status)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const api = {
  authSession: async () => {
    const session = await request<AuthSessionDto>('/api/auth/session')
    sessionCsrfToken = session.csrfToken || readCookie('XSRF-TOKEN')
    return session
  },

  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  bootstrap: () => request<BootstrapDto>('/api/v1/bootstrap'),

  action: (action: UserAction) => request<BootstrapDto>('/api/v1/actions', {
    method: 'POST',
    body: JSON.stringify({
      action,
      idempotencyKey: crypto.randomUUID(),
    }),
  }),

  updateTodayTarget: (targetMinutes: number) => request<BootstrapDto>('/api/v1/today/target', {
    method: 'PATCH',
    body: JSON.stringify({ targetMinutes }),
  }),

  resetTodayTarget: () => request<BootstrapDto>('/api/v1/today/target', { method: 'DELETE' }),

  undo: (reason: string) => request<BootstrapDto>('/api/v1/actions/undo', {
    method: 'POST',
    body: JSON.stringify({ reason }),
  }),

  correctEvent: (eventId: string, occurredAt: string, reason: string) =>
    request<BootstrapDto>(`/api/v1/events/${eventId}/time`, {
      method: 'PATCH',
      body: JSON.stringify({ occurredAt, reason }),
    }),

  saveSettings: (settings: SettingsDto, onboardingComplete = true) => request<SettingsDto>('/api/v1/settings', {
    method: 'PUT',
    body: JSON.stringify({ ...settings, onboardingComplete }),
  }),

  statistics: (from: string, to: string, filters: StatisticsFilters = {}, signal?: AbortSignal) => {
    const query = statisticsQuery(from, to, filters)
    return request<StatsDto>(`/api/v1/statistics?${query}`, { signal })
  },

  history: (from: string, to: string, signal?: AbortSignal) =>
    request<HistoryDto>(`/api/v1/history?from=${from}&to=${to}`, { signal }),

  commutes: (from: string, to: string, signal?: AbortSignal) => {
    const query = new URLSearchParams({ from, to })
    return request<CommuteLegDto[]>(`/api/v1/commutes?${query}`, { signal })
  },

  updateCommuteStats: (commuteId: string, includedInStats: boolean) =>
    request<CommuteLegDto>(`/api/v1/commutes/${commuteId}`, {
      method: 'PATCH',
      body: JSON.stringify({ includedInStats }),
    }),

  updateSession: (sessionId: string, note: string, tags: string) =>
    request<SessionDto>(`/api/v1/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify({ note, tags }),
    }),

  createManualSession: (input: { mode: WorkMode; startedAt: string; endedAt: string; targetMinutes?: number; note?: string; tags?: string }) =>
    request<SessionDto>('/api/v1/sessions/manual', { method: 'POST', body: JSON.stringify(input) }),

  absences: (from: string, to: string) =>
    request<AbsenceDto[]>(`/api/v1/calendar?from=${from}&to=${to}`),

  calendarDays: (from: string, to: string, signal?: AbortSignal) =>
    request<CalendarDayDto[]>(`/api/v1/calendar/days?from=${from}&to=${to}`, { signal }),

  notificationStatus: () => request<NotificationStatusDto>('/api/v1/notifications/status'),

  testNotification: () => request<NotificationStatusDto>('/api/v1/notifications/test', { method: 'POST' }),

  saveAbsence: (date: string, type: AbsenceType, note?: string) =>
    request<AbsenceDto>('/api/v1/calendar', { method: 'POST', body: JSON.stringify({ date, type, note }) }),

  deleteAbsence: (id: string) => request<void>(`/api/v1/calendar/${id}`, { method: 'DELETE' }),

  adminSummary: () => request<AdminSummaryDto>('/api/v1/admin/summary'),

  downloadCsv: async (from: string, to: string, filters: StatisticsFilters = {}) => {
    const response = await fetch(`/api/v1/export.csv?${statisticsQuery(from, to, filters)}`, {
      credentials: 'include', headers: authHeaders(),
    })
    if (response.status === 401) unauthorizedListeners.forEach((listener) => listener())
    if (!response.ok) throw new ApiError('Не удалось сформировать CSV', response.status)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `office-tracker-${from}-${to}.csv`
    link.click()
    URL.revokeObjectURL(url)
  },

  downloadAllData: async () => {
    const response = await fetch('/api/v1/data-export.json', { credentials: 'include', headers: authHeaders() })
    if (response.status === 401) unauthorizedListeners.forEach((listener) => listener())
    if (!response.ok) throw new ApiError('Не удалось подготовить архив данных', response.status)
    downloadBlob(await response.blob(), 'office-tracker-all-data.json')
  },

  deleteAccount: (confirmation: string) => request<void>('/api/v1/account', {
    method: 'DELETE',
    body: JSON.stringify({ confirmation }),
  }),
}

function isStateChanging(method?: string) {
  return method !== undefined && !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())
}

function statisticsQuery(from: string, to: string, filters: StatisticsFilters) {
  const query = new URLSearchParams({ from, to })
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value))
  })
  return query
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`
  const value = document.cookie.split(';').map((item) => item.trim()).find((item) => item.startsWith(prefix))
  if (!value) return undefined
  try { return decodeURIComponent(value.slice(prefix.length)) } catch { return value.slice(prefix.length) }
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
